import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "../../services/api";
import { Badge, thCls, tdCls, btnPrimary, btnGhost, cls, can } from "../ui";
import { Shield, PenLine, ChevronDown, ChevronUp } from "lucide-react";

const U = "/ujms";

function EditCard({ e, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#d5dae8] rounded-sm p-3 mb-2 bg-white" data-testid={`ibr-edit-${idx}`}>
      <div className="flex items-center gap-3 text-[13px]">
        <div><span className="font-bold text-slate-500">Edit #:</span> <span className="font-mono font-bold text-slate-800">{e.edit_number}</span></div>
        <div><span className="font-bold text-slate-500">Edit Type:</span> <span className="font-semibold text-slate-800">{e.edit_type}</span></div>
      </div>
      <div className="text-sm text-slate-700 mt-1.5">{e.message}</div>
      {e.remarks && (
        <button className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-ujms-dark" onClick={() => setOpen(!open)}
          data-testid={`ibr-remarks-toggle-${idx}`}>
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} View Remarks
        </button>
      )}
      {open && <div className="text-xs text-slate-500 mt-1 border-t border-slate-100 pt-1.5" data-testid={`ibr-remarks-${idx}`}>{e.remarks}</div>}
    </div>
  );
}

export default function LECaseView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { access } = useOutletContext() || {};
  const [doc, setDoc] = useState(null);

  const load = useCallback(() => {
    api.get(`${U}/le/incidents/${id}`).then((r) => setDoc(r.data)).catch(() => toast.error("Incident not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.ibr_errors) d.ibr_errors.forEach((er) => toast.error(`Edit ${er.edit_number} — ${er.message}`));
      else toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };

  if (!doc) return <div className="text-sm text-slate-400 p-6">Loading incident…</div>;
  const errs = doc.ibr_errors || [];

  return (
    <div data-testid="le-case-view">
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <Shield className="w-6 h-6 text-ujms-dark" />
        <h1 className="text-2xl font-bold text-slate-900">Case: <span className="font-mono">{doc.incident_number}</span></h1>
        <Badge value={doc.status} />
        {doc.arrest_id && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-sm px-1.5 py-0.5">BOOKED ✓</span>}
        <div className="ml-auto flex gap-1.5">
          <button className={btnGhost} onClick={() => navigate("/officer?tab=incidents")} data-testid="lecv-back">Back to Law enforcement</button>
          {doc.status !== "APPROVED" && (
            <button className={btnGhost} onClick={() => navigate(`/officer/report/${doc.id}`)} data-testid="lecv-open-writing">
              <PenLine className="w-3.5 h-3.5" /> Open in Report Writing
            </button>
          )}
          {["DRAFT", "REJECTED"].includes(doc.status) && (
            <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/le/incidents/${doc.id}/submit`), "Submitted for review")} data-testid="lecv-submit">Submit</button>
          )}
          {doc.status === "SUBMITTED" && (can(access, "le.supervise", "administration.manage") ? (
            <>
              <button className={cls(btnPrimary, "!bg-emerald-700 hover:!bg-emerald-600")} onClick={() => run(() => api.post(`${U}/le/incidents/${doc.id}/review`, { action: "APPROVE" }), "Approved — officer may book")} data-testid="lecv-approve">Approve</button>
              <button className={cls(btnGhost, "!border-orange-300 !text-orange-800")} onClick={() => { const notes = window.prompt("Return to officer — what needs correcting?"); if (notes) run(() => api.post(`${U}/le/incidents/${doc.id}/review`, { action: "REJECT", notes }), "Returned to officer"); }} data-testid="lecv-return">Return</button>
            </>
          ) : <span className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded-sm px-1.5 leading-5" data-testid="lecv-awaiting-sgt">Awaiting Sergeant approval</span>)}
          {doc.status === "APPROVED" && !doc.arrest_id && (
            <button className={btnPrimary} onClick={() => run(() => api.post(`${U}/le/incidents/${doc.id}/convert-to-arrest`), "Booked as arrest — nothing retyped")} data-testid="lecv-book">Book Arrest</button>
          )}
        </div>
      </div>

      <div className="flex gap-5 items-start">
        <div className="flex-1 min-w-0 space-y-5">
          <section data-testid="lecv-offenses">
            <table className="w-full border-collapse border border-[#d5dae8]">
              <thead><tr>
                <th className={thCls}>Offense</th><th className={thCls}>Occurred</th>
                <th className={thCls}>Location</th><th className={thCls}>Reporting Officer</th>
              </tr></thead>
              <tbody><tr>
                <td className={`${tdCls} font-semibold`}>{doc.incident_type}</td>
                <td className={`${tdCls} font-mono text-xs`}>{(doc.occurred_at || "").slice(0, 10)}</td>
                <td className={tdCls}>{doc.location || "—"}</td>
                <td className={tdCls}>{doc.reporting_officer}</td>
              </tr></tbody>
            </table>
          </section>

          <section data-testid="lecv-subjects">
            <table className="w-full border-collapse border border-[#d5dae8]">
              <thead><tr><th className={thCls}>Subject Name</th><th className={thCls}>Subject Type</th></tr></thead>
              <tbody>
                {(doc.persons || []).map((p, i) => (
                  <tr key={i}><td className={`${tdCls} font-semibold text-ujms-dark`}>{p.name}</td><td className={tdCls}>{p.role}</td></tr>
                ))}
                {!(doc.persons || []).length && <tr><td colSpan={2} className={tdCls}>No subjects entered.</td></tr>}
              </tbody>
            </table>
          </section>

          <section data-testid="lecv-vehicles">
            <table className="w-full border-collapse border border-[#d5dae8]">
              <thead><tr><th className={thCls}>Vehicle Plate</th><th className={thCls}>Make / Model</th></tr></thead>
              <tbody>
                {(doc.vehicles || []).map((v, i) => (
                  <tr key={i}><td className={`${tdCls} font-mono text-xs`}>{v.plate || "—"}</td><td className={tdCls}>{v.make_model || "—"}</td></tr>
                ))}
                {!(doc.vehicles || []).length && <tr><td colSpan={2} className={tdCls}>No vehicles entered.</td></tr>}
              </tbody>
            </table>
          </section>

          <section data-testid="lecv-property">
            <table className="w-full border-collapse border border-[#d5dae8]">
              <thead><tr><th className={thCls}>Property / Evidence</th></tr></thead>
              <tbody>
                {(doc.property_items || []).map((p, i) => <tr key={i}><td className={tdCls}>{p}</td></tr>)}
                {!(doc.property_items || []).length && <tr><td className={tdCls}>No property entered.</td></tr>}
              </tbody>
            </table>
          </section>

          <section data-testid="lecv-narrative">
            <table className="w-full border-collapse border border-[#d5dae8]">
              <thead><tr><th className={thCls}>Report Narrative</th></tr></thead>
              <tbody><tr><td className={`${tdCls} whitespace-pre-wrap`}>{doc.narrative || "—"}</td></tr></tbody>
            </table>
          </section>
        </div>

        {/* IBR Case Edit List rail */}
        <aside className="w-[340px] shrink-0 border border-[#d5dae8] rounded-sm bg-[#f7f8fc]" data-testid="ibr-edit-list">
          <div className="px-4 py-3 border-b border-[#d5dae8] flex items-center justify-between">
            <span className="text-[15px] font-bold text-slate-800">IBR Case Edit List ({errs.length})</span>
            <ChevronUp className="w-4 h-4 text-slate-400" />
          </div>
          <div className="p-3">
            {errs.map((e, i) => <EditCard key={i} e={e} idx={i} />)}
            {!errs.length && (
              <div className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="ibr-clean">
                No IBR edits — this report passes all validation checks.
              </div>
            )}
          </div>
        </aside>
      </div>
      <div className="mt-4 text-xs text-slate-400">
        Reported by {doc.reporting_officer} · <Link className="tyler-link" to="/officer" data-testid="le-officer-workspace">Law enforcement</Link>
      </div>
    </div>
  );
}
